import React, { useState, useEffect, useRef, useCallback } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer';
import Newsletter from '../components/Newsletter';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight, faUser, faEnvelope, faPhone, faBoxOpen, faFileAlt,
  faDollarSign, faLink, faHashtag, faSpinner, faExclamationCircle,
  faSearch, faCheckCircle, faCopy, faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import { fetchAllCustomers, createPaymentIntent, fetchUserOrdersByEmail } from '../services/paymentService';

export default function PaymentDetails() {
  const navigate = useNavigate();

  const packageServices = [
    'Embroidery Digitizing', 'Vectorization', 'Website Development',
    'Logo Designing', 'Brandings',
  ];

  // ── State ────────────────────────────────────────────────────────────────
  const [customers,         setCustomers]         = useState([]);
  const [userOrders,        setUserOrders]        = useState([]);
  const [loadingCustomers,  setLoadingCustomers]  = useState(true);
  const [loadingOrders,     setLoadingOrders]     = useState(false);
  // idle | searching | found | not_found
  const [emailStatus,       setEmailStatus]       = useState('idle');
  const [submitting,    setSubmitting]    = useState(false);
  const [error,         setError]         = useState('');
  const [paymentLink,   setPaymentLink]   = useState('');
  const [linkCopied,    setLinkCopied]    = useState(false);

  const [formData, setFormData] = useState({
    selectedCustomer: '',
    userId:           null,
    customerName:     '',
    customerEmail:    '',
    customerPhone:    '',
    packageService:   '',
    packageDetails:   '',
    packagePrice:     '',
    orderNumber:      '',    // final value sent to backend
    selectedOrderId:  '',    // orders.id if picked from dropdown
  });

  const emailDebounceRef = useRef(null);

  // ── Load customers ────────────────────────────────────────────────────────
  useEffect(() => {
    fetchAllCustomers()
      .then(d => setCustomers(d))
      .catch(() => setCustomers([]))
      .finally(() => setLoadingCustomers(false));
  }, []);

  // ── Fetch orders by email ─────────────────────────────────────────────────
  const loadOrders = useCallback(async (email) => {
    if (!email) { setUserOrders([]); return []; }
    setLoadingOrders(true);
    try {
      const orders = await fetchUserOrdersByEmail(email);
      setUserOrders(orders || []);
      return orders || [];
    } catch {
      setUserOrders([]);
      return null; // null = user not found in DB
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  // ── Select existing customer from dropdown ────────────────────────────────
  const handleCustomerSelect = async (e) => {
    const id       = e.target.value;
    const customer = customers.find(c => String(c.id) === String(id));

    if (customer) {
      setFormData(prev => ({
        ...prev,
        selectedCustomer: id,
        userId:           customer.id,
        customerName:     customer.name,
        customerEmail:    customer.email,
        customerPhone:    customer.phone || '',
        orderNumber:      '',
        selectedOrderId:  '',
      }));
      setEmailStatus('found');
      await loadOrders(customer.email);
    } else {
      setFormData(prev => ({
        ...prev,
        selectedCustomer: '',
        userId:           null,
        customerName:     '',
        customerEmail:    '',
        customerPhone:    '',
        orderNumber:      '',
        selectedOrderId:  '',
      }));
      setEmailStatus('idle');
      setUserOrders([]);
    }
  };

  // ── Handle all manual input changes ──────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'customerEmail') {
      // Detach from any selected customer
      setFormData(prev => ({
        ...prev,
        customerEmail:    value,
        selectedCustomer: '',
        userId:           null,
        orderNumber:      '',
        selectedOrderId:  '',
      }));
      setUserOrders([]);

      if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);

      if (!value || !value.includes('@') || !value.includes('.')) {
        setEmailStatus('idle');
        return;
      }

      setEmailStatus('searching');

      emailDebounceRef.current = setTimeout(async () => {
        const orders = await loadOrders(value);

        if (orders !== null) {
          // User exists in DB
          setEmailStatus('found');
          // Try to fill name/phone from customers list if not already typed
          const match = customers.find(c => c.email.toLowerCase() === value.toLowerCase());
          if (match) {
            setFormData(prev => ({
              ...prev,
              userId:       match.id,
              customerName: prev.customerName || match.name,
              customerPhone: prev.customerPhone || match.phone || '',
            }));
          }
        } else {
          // User not in DB — new customer
          setEmailStatus('not_found');
        }
      }, 650);

      return;
    }

    // name / phone — just detach from dropdown selection
    if (['customerName', 'customerPhone'].includes(name)) {
      setFormData(prev => ({ ...prev, [name]: value, selectedCustomer: '', userId: null }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Handle order selection from the order dropdown ────────────────────────
  const handleOrderSelect = (e) => {
    const orderId = e.target.value;
    const order   = userOrders.find(o => String(o.id) === String(orderId));

    if (order) {
      setFormData(prev => ({
        ...prev,
        selectedOrderId: orderId,
        orderNumber:     order.order_number,
        // Auto-fill service from the order if not yet set
        packageService:  prev.packageService || order.service || '',
      }));
    } else {
      setFormData(prev => ({ ...prev, selectedOrderId: '', orderNumber: '' }));
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.customerName.trim() || !formData.customerEmail.trim()) {
      setError('Customer name and email are required.'); return;
    }
    if (!formData.packageService) {
      setError('Please select a service.'); return;
    }
    if (!formData.packagePrice || isNaN(formData.packagePrice) || Number(formData.packagePrice) < 0.50) {
      setError('Please enter a valid package price (minimum $0.50).'); return;
    }
    if (!formData.orderNumber.trim()) {
      setError('Please select or enter an order number.'); return;
    }

    try {
      setSubmitting(true);
      const result = await createPaymentIntent({
        userId:         formData.userId,
        customerName:   formData.customerName,
        customerEmail:  formData.customerEmail,
        customerPhone:  formData.customerPhone,
        packageService: formData.packageService,
        packageDetails: formData.packageDetails,
        packagePrice:   parseFloat(formData.packagePrice),
        orderNumber:    formData.orderNumber,
        linkedOrderId:  formData.selectedOrderId || null,
      });

      // Build the shareable link from the token
      const link = `${window.location.origin}/complete-payment/${result.paymentToken}`;
      setPaymentLink(link);
      // Scroll up so the link banner is visible
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const showOrderDropdown = emailStatus === 'found' && userOrders.length > 0;
  const showNoOrdersNote  = emailStatus === 'found' && userOrders.length === 0 && !loadingOrders;
  const showNewUserNote   = emailStatus === 'not_found';

  return (
    <>
      <style>{`
        .pd-error-banner {
          background: #fff1f0; border: 1.5px solid #ffc2be; border-radius: 10px;
          padding: 11px 16px; font-size: 13.5px; color: #c0392b; font-weight: 600;
          margin-bottom: 20px; display: flex; align-items: center; gap: 8px;
        }
        .pd-email-pill {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 12px; font-weight: 700; padding: 4px 10px;
          border-radius: 20px; margin-top: 7px;
        }
        .pd-email-pill.found     { background: #e6f9f0; color: #0e8a5f; }
        .pd-email-pill.not-found { background: #fff8e1; color: #d97706; }
        .pd-email-pill.searching { background: #e0f0ff; color: #1d6a88; }

        .pd-order-box {
          background: #f0f9ff; border: 1.5px solid #bae6fd;
          border-radius: 12px; padding: 16px 20px; margin-bottom: 10px;
        }
        .pd-order-box-label {
          font-size: 11px; font-weight: 700; color: #0369a1;
          text-transform: uppercase; letter-spacing: 0.08em;
          margin: 0 0 10px; display: flex; align-items: center; gap: 6px;
        }
        .pd-note {
          border-radius: 10px; padding: 10px 14px; font-size: 13px;
          font-weight: 500; display: flex; align-items: center; gap: 8px;
          margin-bottom: 10px;
        }
        .pd-note.info   { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; }
        .pd-note.warn   { background: #fffbeb; color: #92400e; border: 1px solid #fcd34d; }
        .pd-divider-hint {
          font-size: 12px; color: #8a99ad; margin: 8px 0 0;
        }
        .required { color: #e53e3e; font-size: 11px; margin-left: 2px; }

        /* Payment link success banner */
        .pd-link-banner {
          background: #e6f9f0; border: 2px solid #6ee7b7;
          border-radius: 14px; padding: 20px 22px; margin-bottom: 24px;
          animation: pd-slidein 0.35s ease;
        }
        @keyframes pd-slidein {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .pd-link-banner-top {
          display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px;
        }
        .pd-link-banner-icon { font-size: 22px; color: #059669; margin-top: 2px; flex-shrink: 0; }
        .pd-link-banner-title { margin: 0 0 3px; font-size: 15px; font-weight: 800; color: #065f46; }
        .pd-link-banner-sub   { margin: 0; font-size: 13px; color: #047857; }
        .pd-link-row {
          display: flex; align-items: center; gap: 10px;
          background: #fff; border: 1.5px solid #a7f3d0;
          border-radius: 10px; padding: 10px 14px; flex-wrap: wrap;
        }
        .pd-link-text {
          flex: 1; font-size: 13px; font-weight: 600; color: #002536;
          word-break: break-all; font-family: monospace;
        }
        .pd-link-actions { display: flex; gap: 8px; flex-shrink: 0; }
        .pd-link-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 16px; border-radius: 8px; font-size: 13px;
          font-weight: 700; cursor: pointer; border: none;
          transition: all 0.2s ease; font-family: inherit;
        }
        .pd-link-btn-copy {
          background: #002536; color: #fff;
        }
        .pd-link-btn-copy:hover { background: #1d6a88; }
        .pd-link-btn-open {
          background: transparent; color: #047857;
          border: 1.5px solid #6ee7b7 !important;
          text-decoration: none;
        }
        .pd-link-btn-open:hover { background: #d1fae5; }
      `}</style>

      <Header />

      <section className="breadcrumbsection">
        <div className='container'>
          <h1>Payment Details</h1>
          <ul className='breadcrumblinks'>
            <li><Link to="/">Home</Link></li>
            <li><FontAwesomeIcon icon={faChevronRight} /></li>
            <li>Payment Details</li>
          </ul>
        </div>
      </section>

      <section className="payment-main-section">
        <div className="container">
          <div className="payment-form-wrapper">

            <div className="payment-form-header">
              <h2>Create Payment Link</h2>
              <p>Select a customer to link their order and generate a payment</p>
            </div>

            {error && (
              <div className="pd-error-banner">
                <FontAwesomeIcon icon={faExclamationCircle} /> {error}
              </div>
            )}

            {/* ── Payment link success banner ── */}
            {paymentLink && (
              <div className="pd-link-banner">
                <div className="pd-link-banner-top">
                  <FontAwesomeIcon icon={faCheckCircle} className="pd-link-banner-icon" />
                  <div>
                    <p className="pd-link-banner-title">Payment link created!</p>
                    <p className="pd-link-banner-sub">Share this link with the customer to complete payment.</p>
                  </div>
                </div>
                <div className="pd-link-row">
                  <span className="pd-link-text">{paymentLink}</span>
                  <div className="pd-link-actions">
                    <button
                      type="button"
                      className="pd-link-btn pd-link-btn-copy"
                      onClick={() => {
                        navigator.clipboard.writeText(paymentLink);
                        setLinkCopied(true);
                        setTimeout(() => setLinkCopied(false), 2500);
                      }}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                      {linkCopied ? 'Copied!' : 'Copy'}
                    </button>
                    <a
                      href={paymentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pd-link-btn pd-link-btn-open"
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                      Open
                    </a>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="payment-form">

              {/* ── Select Existing Customer ───────────────────────────── */}
              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="selectedCustomer">
                    <FontAwesomeIcon icon={faUser} className="field-icon" />
                    Select Existing Customer
                  </label>
                  <select
                    id="selectedCustomer"
                    value={formData.selectedCustomer}
                    onChange={handleCustomerSelect}
                    className="form-control"
                    disabled={loadingCustomers}
                  >
                    <option value="">
                      {loadingCustomers ? 'Loading customers...' : '— Select a customer —'}
                    </option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} — {c.email}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-divider"><span>OR Enter Details Manually</span></div>

              {/* ── Customer Information ───────────────────────────────── */}
              <div className="form-section">
                <h3 className="section-title">Customer Information</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="customerName">
                      <FontAwesomeIcon icon={faUser} className="field-icon" />
                      Customer Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="customerName"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Enter customer name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="customerEmail">
                      <FontAwesomeIcon icon={faEnvelope} className="field-icon" />
                      Customer Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="customerEmail"
                      name="customerEmail"
                      value={formData.customerEmail}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="customer@example.com"
                    />
                    {/* Status pill under email field */}
                    {emailStatus === 'searching' && (
                      <span className="pd-email-pill searching">
                        <FontAwesomeIcon icon={faSpinner} spin /> Looking up...
                      </span>
                    )}
                    {emailStatus === 'found' && !formData.selectedCustomer && (
                      <span className="pd-email-pill found">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        Existing customer
                        {userOrders.length > 0 ? ` · ${userOrders.length} order${userOrders.length !== 1 ? 's' : ''}` : ' · no orders yet'}
                      </span>
                    )}
                    {emailStatus === 'found' && formData.selectedCustomer && (
                      <span className="pd-email-pill found">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        {userOrders.length > 0 ? `${userOrders.length} order${userOrders.length !== 1 ? 's' : ''} found` : 'No orders yet'}
                      </span>
                    )}
                    {emailStatus === 'not_found' && (
                      <span className="pd-email-pill not-found">
                        ✨ New customer — account will be created
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label htmlFor="customerPhone">
                      <FontAwesomeIcon icon={faPhone} className="field-icon" />
                      Customer Phone Number
                    </label>
                    <input
                      type="tel"
                      id="customerPhone"
                      name="customerPhone"
                      value={formData.customerPhone}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* ── Package & Order ────────────────────────────────────── */}
              <div className="form-section">
                <h3 className="section-title">Package Information</h3>

                {/* Order Number — smart field */}
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>
                      <FontAwesomeIcon icon={faHashtag} className="field-icon" />
                      Order Number <span className="required">*</span>
                    </label>

                    {/* Loading orders spinner */}
                    {loadingOrders && (
                      <div className="pd-note info">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Loading orders for this customer...
                      </div>
                    )}

                    {/* Order dropdown for existing users WITH orders */}
                    {showOrderDropdown && (
                      <div className="pd-order-box">
                        <p className="pd-order-box-label">
                          <FontAwesomeIcon icon={faSearch} />
                          Select from customer's orders
                        </p>
                        <select
                          className="form-control"
                          value={formData.selectedOrderId}
                          onChange={handleOrderSelect}
                        >
                          <option value="">— Choose an order —</option>
                          {userOrders.map(order => (
                            <option key={order.id} value={order.id}>
                              {order.order_number} &nbsp;·&nbsp; {order.service}
                              {order.status ? `  (${order.status.replace('_', ' ')})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* No orders note for existing user */}
                    {showNoOrdersNote && (
                      <div className="pd-note info">
                        ℹ️ This customer has no orders yet. Enter an order number manually below.
                      </div>
                    )}

                    {/* New user note */}
                    {showNewUserNote && (
                      <div className="pd-note warn">
                        ✨ New customer — a portal account will be created. Enter a reference number below.
                      </div>
                    )}

                    {/* Always-visible text input */}
                    <input
                      type="text"
                      name="orderNumber"
                      value={formData.orderNumber}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder={
                        showOrderDropdown
                          ? 'Auto-filled from selection above, or type to override'
                          : 'e.g. ORD-2025-0042'
                      }
                    />
                    {showOrderDropdown && (
                      <p className="pd-divider-hint">
                        Selecting an order above auto-fills this field. You can also type a custom value.
                      </p>
                    )}
                  </div>
                </div>

                {/* Service + Price */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="packageService">
                      <FontAwesomeIcon icon={faBoxOpen} className="field-icon" />
                      Package Service <span className="required">*</span>
                    </label>
                    <select
                      id="packageService"
                      name="packageService"
                      value={formData.packageService}
                      onChange={handleInputChange}
                      className="form-control"
                    >
                      <option value="">— Select a service —</option>
                      {packageServices.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="packagePrice">
                      <FontAwesomeIcon icon={faDollarSign} className="field-icon" />
                      Package Price (USD) <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      id="packagePrice"
                      name="packagePrice"
                      value={formData.packagePrice}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="0.00"
                      step="0.01"
                      min="0.50"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label htmlFor="packageDetails">
                      <FontAwesomeIcon icon={faFileAlt} className="field-icon" />
                      Package Details
                    </label>
                    <textarea
                      id="packageDetails"
                      name="packageDetails"
                      value={formData.packageDetails}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Enter package details, description, or any special notes..."
                      rows="4"
                    />
                  </div>
                </div>
              </div>

              {/* ── Submit ── */}
              <div className="form-actions">
                <button type="submit" className="payment-submit-btn" disabled={submitting}>
                  {submitting
                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</>
                    : <><FontAwesomeIcon icon={faLink} /> Proceed to Payment</>
                  }
                </button>
              </div>

            </form>
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </>
  );
}